import isEqual from 'lodash.isequal';

import Logger from 'wanderu-logger';
import currencyService from 'wanderu-service-currency';

import { I18nMoney } from '../../libs/i18nMoney';

const logger = Logger.getLogger('CurrencyCoordinator');
function _noop() {}

class CurrencyCoordinator {
    constructor(i18nDataStore) {
        this._currencyChangeHandle = undefined;
        this._onSupportedCurrenciesChange = _noop;
        this._supportedCurrencies = [];
        this.i18nDataStore = i18nDataStore;
    }

    init(opts) {
        const {
            currency,
            defaultCurrency,
        } = opts;

        this.i18nDataStore.setCurrency(currency);
        this.i18nDataStore.setDefaultCurrency(defaultCurrency);
        I18nMoney.setCurrency(currency);
    }

    hydrate(supportedCurrencies, currencyConversionRates) {
        currencyService.hydrate(supportedCurrencies, currencyConversionRates);
        const currency = this.i18nDataStore.getCurrency();

        I18nMoney.setCurrency(currency);
    }

    async initI18nLibraries(currency) {
        try {
            this._assertAllAppsSupportCurrency(currency);
            const defaultCurrency = this.i18nDataStore.getDefaultCurrency();

            const currencyPromise = currencyService.loadConversionRatesForCurrency(currency);
            let defaultCurrencyPromise = Promise.resolve();
            if (defaultCurrency !== currency) {
                defaultCurrencyPromise = currencyService.loadConversionRatesForCurrency(
                    defaultCurrency,
                );
            }
            await Promise.all([
                currencyPromise,
                defaultCurrencyPromise,
            ]);
        } catch (ex) {
            logger.warn('initI18nLibraries', ex, { currency });
        }
    }

    registerListeners(opts) {
        const {
            onCurrencyChange = _noop,
            onSupportedCurrenciesChange = _noop,
        } = opts;

        this._currencyChangeHandle = this.i18nDataStore
            .addCurrencyChangeListener(onCurrencyChange);
        this._onSupportedCurrenciesChange = onSupportedCurrenciesChange;
    }

    unregisterListeners() {
        this.i18nDataStore
            .removeCurrencyChangeListener(this._currencyChangeHandle);
        this._onSupportedCurrenciesChange = _noop;
    }

    async update(appConfig) {
        this._updateSupportedCurrencies();

        const currency = this.i18nDataStore.getCurrency();

        try {
            this._assertAllAppsSupportCurrency(currency);
        } catch (ex) {
            logger.warn('update currencyCoordinator', ex, { currency });
            await this._notifyCurrencyChange(appConfig);
        }
    }

    async updateCurrency(currency, { silent = false } = {}) {
        const currentCurrency = this.i18nDataStore.getCurrency();

        if (currency === currentCurrency) {
            logger.info('updateCurrency', `${currency} is already the current currency`);
            return;
        }
        // eslint-disable-next-line no-useless-catch
        try {
            this._assertAllAppsSupportCurrency(currency);
            await currencyService.loadConversionRatesForCurrency(currency);
        } catch (ex) {
            throw ex;
        }

        this.i18nDataStore.setCurrency(currency);
        I18nMoney.setCurrency(currency);

        if (silent) {
            return;
        }
        this._notifyChangeListeners(currency, currentCurrency);
    }

    async _notifyCurrencyChange(appConfig) {
        const compatibleCurrency = this._getCompatibleCurrency(appConfig);
        await this.updateCurrency(compatibleCurrency);
    }

    _notifyChangeListeners(currency, prevCurrency) {
        const changeListeners = this.i18nDataStore.getCurrencyChangeListeners();
        Object.entries(changeListeners).forEach(([handle, listener]) => {
            // Listeners can cause other listeners to unregister
            // and getCurrencyChangeListeners returns an immutable object
            if (!this.i18nDataStore.currencyChangeListenerExists(handle)) {
                return;
            }

            listener(currency, prevCurrency);
        });
    }

    _getCompatibleCurrency(appConfig) {
        // Try current currency
        const currency = this.i18nDataStore.getCurrency();

        try {
            this._assertAllAppsSupportCurrency(currency);
            return currency;
        } catch (ex) {
            logger.warn('_getCompatibleCurrency', ex, { currency });
        }

        // Try global default currency
        try {
            const defaultCurrency = this.i18nDataStore.getDefaultCurrency();
            this._assertAllAppsSupportCurrency(defaultCurrency);
            return defaultCurrency;
        } catch (ex) {
            logger.warn('_getCompatibleCurrency', ex, { currency });
        }

        // Return first currency in supported currencies
        return this._supportedCurrencies[0];
    }

    _updateSupportedCurrencies() {
        const prevSupportedCurrencies = this._supportedCurrencies;
        this._supportedCurrencies = this._getIntersectionOfSupportedCurrencies();

        const sameSupportedCurrencies = isEqual(this._supportedCurrencies, prevSupportedCurrencies);

        if (sameSupportedCurrencies) {
            return;
        }

        this._onSupportedCurrenciesChange(this._supportedCurrencies);
    }

    _getIntersectionOfSupportedCurrencies() {
        const appConfigs = this.i18nDataStore.getAppConfigs();

        if (Object.keys(appConfigs).length === 0) {
            return appConfigs;
        }

        return Object.values(appConfigs)
            .reduce((currentSet, appConfig) => {
                const appCurrencySet = appConfig.supportedCurrencies;
                if (currentSet.length === 0) {
                    return [...appCurrencySet];
                }
                return currentSet.filter((currency) => appCurrencySet.includes(currency));
            }, []);
    }

    _assertAllAppsSupportCurrency(currency) {
        const appConfigs = this.i18nDataStore.getAppConfigs();

        const appNamesThatDoNotSupportCurrency = Object.values(appConfigs)
            .reduce((appNames, appConfig) => {
                const doesNotSupport = !appConfig.supportedCurrencies.includes(currency);
                if (doesNotSupport) {
                    return [...appNames, appConfig.namespace];
                }
                return appNames;
            }, []);

        if (appNamesThatDoNotSupportCurrency.length > 0) {
            throw new Error(`${appNamesThatDoNotSupportCurrency} do(es) not support ${currency}`);
        }
    }
}

export default CurrencyCoordinator;
